'use client';

import { useEffect, useLayoutEffect, useSyncExternalStore } from 'react';
import type { OrgChatArtifactPanelState } from '@/components/org-chat/org-chat-types';
import {
  isTouchPrimaryEnvironment,
  readThreadPanelLayoutHint,
  writeThreadPanelLayoutHint,
} from '@/lib/thread-panel-layout-hint';
import { useThreadPanelLayoutHintScope } from '@/components/org-chat/use-thread-presentation-hint-scope';
import {
  localStorageOrNull,
  writeScopedPresentationHint,
} from '@/lib/scoped-presentation-hint';
import {
  readThreadTranscriptShapeHint,
  THREAD_SKELETON_SHAPE_ATTR,
} from '@/lib/thread-transcript-shape-hint';
import {
  THREAD_PANEL_HINT_IDENTITY_KEY,
  THREAD_PANEL_RESERVED_ATTR,
} from '@/lib/thread-panel-reserve-boot';
import { resolveRestorableArtifactPanel } from './artifact-panel-restore';
import { useArtifactDrawerLayout } from '@/components/org-chat/interface/use-artifact-drawer-layout';

const noopSubscribe = () => () => {};

/**
 * Whether thread skeletons should reserve the docked artifact panel column.
 * Reads synchronously on the client; server rendering and hydration use
 * `false`, so markup never mismatches.
 */
export function useReserveDockedArtifactPanel(): boolean {
  const scope = useThreadPanelLayoutHintScope();
  const drawerLayout = useArtifactDrawerLayout();
  const hasDockedHint = useSyncExternalStore(
    noopSubscribe,
    () => readThreadPanelLayoutHint(localStorageOrNull(), scope),
    () => false
  );
  const reserve = !drawerLayout && hasDockedHint;
  // The boot script marked <html> before first paint; keep the mark true to
  // this thread on client navigations (the reservation's CSS reads it). Read
  // directly: during hydration the hook values are still the server's false.
  const { identity, orgSlug, threadRouteRef } = scope;
  useLayoutEffect(() => {
    // Before the identity is confirmed there is no hint to read; the boot
    // script's mark stands until then.
    if (!identity) return;
    // So the next cold load's boot script can read this identity's hints.
    writeScopedPresentationHint(
      localStorageOrNull(),
      THREAD_PANEL_HINT_IDENTITY_KEY,
      identity
    );
    const shape = readThreadTranscriptShapeHint(localStorageOrNull(), {
      identity,
      orgSlug,
      threadRouteRef,
    });
    if (shape) {
      document.documentElement.setAttribute(THREAD_SKELETON_SHAPE_ATTR, shape);
    } else {
      document.documentElement.removeAttribute(THREAD_SKELETON_SHAPE_ATTR);
    }
    const docked =
      !window.matchMedia('(max-width: 1023px)').matches &&
      !isTouchPrimaryEnvironment({
        hasTouchStart: 'ontouchstart' in window,
        maxTouchPoints: window.navigator.maxTouchPoints,
        matchMedia: (query) => window.matchMedia(query),
      }) &&
      readThreadPanelLayoutHint(localStorageOrNull(), {
        identity,
        orgSlug,
        threadRouteRef,
      });
    document.documentElement.toggleAttribute(
      THREAD_PANEL_RESERVED_ATTR,
      docked
    );
    // `reserve` re-runs it when the viewport crosses the drawer breakpoint.
  }, [identity, orgSlug, threadRouteRef, reserve]);
  return reserve;
}

/**
 * Mirrors the thread's server-persisted panel state into the layout hint once
 * the thread has loaded. `undefined` (not loaded yet) never writes, so an
 * initial default cannot overwrite a hint.
 */
export function useRecordThreadPanelLayoutHint(
  artifactPanelState: OrgChatArtifactPanelState | null | undefined,
  threadLoaded: boolean
): void {
  const scope = useThreadPanelLayoutHintScope();
  const { identity, orgSlug, threadRouteRef } = scope;
  const docked = resolveRestorableArtifactPanel(artifactPanelState) !== null;
  useEffect(() => {
    if (!threadLoaded) return;
    // The boot script reads hints under this identity (it runs before any
    // provider knows who is signed in).
    writeScopedPresentationHint(
      localStorageOrNull(),
      THREAD_PANEL_HINT_IDENTITY_KEY,
      identity
    );
    writeThreadPanelLayoutHint(
      localStorageOrNull(),
      { identity, orgSlug, threadRouteRef },
      docked
    );
  }, [docked, identity, orgSlug, threadLoaded, threadRouteRef]);
}
